import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { temSessao } from 'services/sessao';
import { rotaDoPush } from 'services/destinoDaNotificacao';
import {
	instalarOuvintesPush,
	marcarNotificacaoTocada,
	pushDisponivel,
	sincronizarPush,
} from 'services/pushNotificacoes';

/** Onde o pedido de permissão pode aparecer: o painel, a primeira tela depois do login. */
const ROTA_DO_PEDIDO = '/dashboard';

/**
 * Notificação push no app (services/pushNotificacoes.js), presa ao ciclo de vida das telas.
 *
 * Três momentos sincronizam o aparelho com o backend:
 *
 * 1. **Chegada ao painel com sessão** — o único que pode PEDIR a permissão. É a memória "atrito
 *    mínimo no funil": o pedido não aparece na instalação, no login nem no meio do cadastro; aparece
 *    quando a pessoa já está dentro e tem motivo para querer ser avisada.
 * 2. **Sessão que passa a existir em outra tela** — confirma o que já foi decidido, sem perguntar.
 * 3. **Volta do app ao primeiro plano** — a pessoa pode ter ligado ou desligado as notificações nas
 *    configurações do Android. Ligou: o token volta ao backend. Desligou: sai, e o SMS volta a valer.
 *
 * Fica em AppRoutes, ao lado do useBotaoVoltarNativo, pelo mesmo motivo: precisa do `navigate`.
 */
export function usePushNativo() {
	const navigate = useNavigate();
	const { pathname } = useLocation();

	// `temSessao` é síncrono e lido a cada render: o login termina com uma navegação, que re-renderiza
	// AppRoutes, e é aí que `logado` vira true.
	const disponivel = pushDisponivel();
	const logado = disponivel && temSessao();
	const noPainel = pathname === ROTA_DO_PEDIDO;

	const navigateRef = useRef(navigate);
	navigateRef.current = navigate;

	// Ouvintes: uma vez, com ou sem sessão (ver instalarOuvintesPush sobre o toque que abre o app).
	useEffect(() => {
		if (!disponivel) return undefined;

		let remover;
		let cancelado = false;

		(async () => {
			try {
				const removerOuvintes = await instalarOuvintesPush({
					// O toque leva ao assunto do aviso (services/destinoDaNotificacao.js): o agendamento,
					// a tela de reenvio do documento, a assinatura. Aviso sem destino — ou de um tipo que
					// esta versão do app não conhece — cai no painel, onde ele também aparece em contexto.
					// Sem sessão, a guarda de rota leva ao login e devolve ao destino depois (App.jsx).
					aoTocar: (dados) => {
						marcarNotificacaoTocada(dados?.notificacaoId);
						navigateRef.current(rotaDoPush(dados) || '/dashboard');
					},
				});

				const { App } = await import('@capacitor/app');
				const inscricaoApp = await App.addListener('appStateChange', ({ isActive }) => {
					if (isActive && temSessao()) sincronizarPush();
				});

				remover = () => {
					if (typeof removerOuvintes === 'function') removerOuvintes();
					inscricaoApp?.remove?.();
				};
				if (cancelado) remover();
			} catch {
				// Plugin sem implementação (navegador com o bundle nativo, Jest): sem push, e o app
				// segue igual. É o mesmo efeito de a pessoa negar a permissão.
			}
		})();

		return () => {
			cancelado = true;
			remover?.();
		};
	}, [disponivel]);

	useEffect(() => {
		if (!logado) return;
		sincronizarPush({ podePerguntar: noPainel });
	}, [logado, noPainel]);
}

export default usePushNativo;
